import { ErrorResponse, fetchAPI } from '@quickbit/backoffice-core-frontend';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface FileItem {
  url: string;
  name: string;
}

export interface ContactItem {
  contactId: string;
  merchantId: string;
  merchantName: string;
  label: string;
  address: string;
  currencyCode: string;
  travelRuleData: Record<string, unknown> | null;
  files: FileItem[];
}

export interface ManualReviewTransactionItem {
  transactionId: string;
  userId: string;
  currency: string;
  amount: string;
  transactionType: string;
  travelRuleData: Record<string, unknown> | null;
  files: FileItem[];
}

export interface PendingProofOfOwnershipResponse {
  contacts: ContactItem[];
  transactions: ManualReviewTransactionItem[];
}

const queryKeys = {
  all: () => ['fcu'] as const,
  pendingItems: () => [...queryKeys.all(), 'pending-items'] as const,
};

export const useGetPendingItems = () => {
  return useQuery<PendingProofOfOwnershipResponse, ErrorResponse>({
    queryKey: queryKeys.pendingItems(),
    queryFn: async () => {
      const response = await fetchAPI<PendingProofOfOwnershipResponse>(
        'v1/fcu/pending-proof-of-ownership'
      );
      return response;
    },
  });
};

export const useApproveSelfHostedProofOfOwnership = ({
  merchantId,
}: {
  merchantId?: string;
}) => {
  const queryClient = useQueryClient();
  return useMutation<unknown, ErrorResponse, { recipientId: string }>({
    mutationFn: async ({ recipientId }) => {
      return await fetchAPI(
        `v1/merchants/${merchantId}/wallet-recipients/${recipientId}/proof-of-ownership`,
        {
          method: 'PATCH',
        }
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.all(),
      });
    },
  });
};
